import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import BrokerPlanGuidanceBanner from '@/components/marketing/BrokerPlanGuidanceBanner';
import FullAccessWaitlistModal, { type WaitlistPlan } from '@/components/marketing/FullAccessWaitlistModal';

import { supabase } from '@/integrations/supabase/client';
import { displayCompanyName } from '@/lib/displayCompanyName';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePageMeta } from '@/hooks/usePageMeta';
import LandingHeader from '@/components/landing/LandingHeader';
import lanerunnerLogoFooter from '@/assets/lanerunner-logo-footer.png.asset.json';
import { 
  ArrowRight, Check, Minus, Shield, MessageSquare, CreditCard, ChevronUp, Zap,
  Facebook, Instagram, Linkedin, Twitter, Truck, Building2, Package, Layers, Crown, DollarSign
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { useSearchParams } from 'react-router-dom';

const lanerunnerLogoWhite = lanerunnerLogoFooter.url;

type RoleKey = 'broker' | 'carrier' | 'shipper';

// ── Pricing selection (highlight + persistence) ─────────────────
// NOTE: `broker_yearly`, `lr_direct_private`, `lr_direct_full` are kept for
// backward compatibility with prior sessionStorage values. They all now route
// to the Broker tab (LR Direct merged into Broker as the 3rd card).
export type SelectionId =
  | 'broker_free' | 'broker_premium' | 'broker_yearly' | 'broker_board_only'
  | 'carrier_free' | 'carrier_premium' | 'carrier_yearly'
  | 'shipper'
  | 'lr_direct_private' | 'lr_direct_full';

const SELECTION_KEY = 'lr_pricing_selection_v1';
const SELECTION_TO_TAB: Record<SelectionId, RoleKey> = {
  broker_free: 'broker', broker_premium: 'broker', broker_yearly: 'broker',
  broker_board_only: 'broker',
  carrier_free: 'carrier', carrier_premium: 'carrier', carrier_yearly: 'carrier',
  shipper: 'shipper',
  lr_direct_private: 'broker', lr_direct_full: 'broker',
};
const isSelectionId = (v: string | null | undefined): v is SelectionId =>
  !!v && v in SELECTION_TO_TAB;
const writeSelection = (s: SelectionId) => {
  try { sessionStorage.setItem(SELECTION_KEY, s); } catch {}
};
export const setPricingSelection = writeSelection; // re-export for landing pages

const SelectedBadge: React.FC<{ className?: string }> = ({ className }) => (
  <span className={cn(
    'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase bg-[#02857E] text-white shadow-sm',
    className
  )}>
    ★ Your Selection
  </span>
);

interface MatrixTier {
  name: string;
  price: string;
  badge?: string | null;
  badgeColor?: string;
  ctaLabel: string;
  ctaHref: string;
  trial: string;
}

interface MatrixRow {
  feature: string;
  description?: string;
  isNew?: boolean;
  isAi?: boolean;
  values: boolean[];
}

interface RoleMatrix {
  key: RoleKey;
  label: string;
  heading: string;
  planLabel: string;
  tiers: MatrixTier[];
  rows: MatrixRow[];
  footnote: string;
}

const CARRIER_ROWS: MatrixRow[] = [
  { feature: 'Find Loads', description: 'Unlimited load searching', values: [true, true, true] },
  { feature: 'Truck Posting', description: 'Unlimited truck posting', values: [true, true, true] },
  { feature: 'Web & Mobile-Optimized Access', description: 'Fully responsive web app — use it on phone, tablet, or desktop', values: [true, true, true] },
  { feature: 'Real-Time Updates', description: 'Auto-refresh live loads', values: [true, true, true] },
  { feature: 'Live Load Alerts & Audio Ping', description: '30s green highlight + sound for new matches', values: [true, true, true] },
  { feature: 'Digital Freight Matching', description: 'Bidding, messaging, and book now', values: [true, true, true] },
  { feature: 'Document Capturing & Storage', values: [true, true, true] },
  { feature: 'Backhauls', description: 'Find return loads', values: [false, true, true] },
  { feature: 'Saved Searches', description: 'One-click recall of your favorite lanes', values: [false, true, true] },
  { feature: 'Distance, ETA & Route Maps', description: 'Mapbox-powered routing on every load', values: [false, true, true] },
  { feature: 'Distance & Driving Time', values: [false, true, true] },
  { feature: 'Credit Scores & Days-to-Pay', values: [false, true, true] },
  { feature: 'Multi-Stop Load Support', description: 'Up to 5 picks / 5 drops per load', values: [false, true, true] },
  { feature: 'Boosted Loads Highlighting', description: 'See premium loads first, in real-time', values: [false, false, true] },
  { feature: 'Carrier View Insights', description: 'See how many carriers viewed each load', values: [false, false, true] },
  { feature: 'Rate Check', description: 'Lane rate insights', values: [false, false, true] },
  { feature: 'Historical Rate Trends', description: 'Insights to visualize market rate trends', values: [false, false, true] },
  { feature: 'Negotiation Strength', description: 'Decision tools to help you negotiate better', values: [false, false, true] },
  { feature: 'Sureline Factoring Integration', description: 'One-click factoring on awarded loads', values: [false, false, true] },
];

const BROKER_ROWS: MatrixRow[] = [
  { feature: 'Unlimited Load Posting', description: 'No caps — post as many as your operation runs', values: [true, true, true] },
  { feature: 'Carrier & Truck Search', description: "Find available capacity across LaneRunner's network", values: [true, true, true] },
  { feature: 'Mobile-Optimized Workspace', description: 'Full broker tools on phone, tablet, or desktop', values: [true, true, true] },
  { feature: 'Live Truck Feed', description: 'Active capacity refreshes instantly — no clicking refresh', values: [true, true, true] },
  { feature: 'Truck Alerts', description: 'Real-time pings when matching capacity posts', values: [true, true, true] },
  
  { feature: 'Document Storage', description: 'Upload, attach, and retrieve docs per load', values: [true, true, true] },
  { feature: 'FMCSA Verification & Trust Scores', description: 'Vet every carrier in seconds with built-in compliance checks', isAi: true, values: [true, true, true] },
  { feature: 'Saved Truck Searches', description: 'Pin your regular lanes for instant recall', values: [true, true, true] },
  { feature: 'Mapbox Routing & ETA', description: 'Distance, route, and arrival times on every load', isAi: true, values: [false, true, true] },
  { feature: 'Drive-Time Calculations', description: 'See accurate driving hours, not just miles', values: [false, true, true] },
  { feature: 'Carrier Metrics', description: 'Make smarter booking calls with safety and performance data inline', values: [false, true, true] },
  { feature: 'AI Voice Post', description: 'Speak your loads in — Gemini Flash handles the rest', isAi: true, values: [false, true, true] },
  { feature: 'AI Bulk Upload', description: 'Drop in a spreadsheet, post up to 100 loads at once', isAi: true, values: [false, true, true] },
  { feature: 'Seat & Team Management', description: 'Invite teammates, control seats and access', values: [false, true, true] },
  { feature: 'ATLAS AI Rate Insights', description: 'AI-driven rate intelligence built into every search', isAi: true, values: [false, true, true] },
  { feature: 'Rate Check', description: 'Instant lane benchmarks before you commit', isAi: true, values: [false, true, true] },
  { feature: 'Historical Rate Trends', description: 'Visualize where lanes have been heading', values: [false, true, true] },
  { feature: 'Lane Predictor & Forecasting', description: 'See where rates are likely going next', isAi: true, values: [false, true, true] },
  { feature: 'Pattern Detector & Repricing', description: 'Auto-flagged opportunities to reprice for margin', isAi: true, values: [false, true, true] },
  { feature: 'ATLAS Command Center', description: 'Templates, repricing, and optimization — one hub', isAi: true, values: [false, true, true] },
  { feature: 'Market Pressure Index', description: 'Live tightness score per lane — know when to push', isAi: true, values: [false, true, true] },
  { feature: 'Boosted Loads', description: 'Surface priority loads at the top of carrier search', values: [false, true, true] },
  { feature: 'Live Load Room', description: 'Real-time auction environment for posted loads', values: [false, true, true] },
  { feature: 'Credit Profile Auto-Fill', description: 'Carriers auto-fill your credit app in one click', isAi: true, values: [false, true, true] },
  { feature: 'Priority Support', description: 'Real humans, faster response when you need it', values: [false, true, true] },
  { feature: 'LR Direct Private Network Bidding', description: 'Unlimited bids on shipper-invited private network loads', isNew: true, values: [false, true, true] },
  { feature: 'Direct Shipper Messaging on Awarded Loads', values: [false, true, true] },
  { feature: 'In-Platform Messaging', description: 'Direct chat with carriers — negotiate, confirm, book', isAi: true, values: [false, false, false] },
  // Folded in from the discontinued $249.99 tier — now part of Full Access ($99.99).
  { feature: 'Open Marketplace LR Direct Bidding', description: 'Bid on every open LR Direct opportunity — the killer feature', isNew: true, values: [false, true, true] },
  { feature: 'Monthly Boost Credits', description: '20 boost credits per month', values: [false, true, true] },
  { feature: 'Dedicated Account Manager', description: 'A single point of contact who knows your operation', isNew: true, values: [false, true, true] },
  { feature: 'Early Access to New Features', description: 'Ship-ahead access to new tools', values: [false, true, true] },
];




const ROLE_MATRIX: Record<RoleKey, RoleMatrix> = {
  carrier: {
    key: 'carrier',
    label: 'Carrier',
    heading: 'Choose your plan and get started today',
    planLabel: 'Carrier Plans',
    tiers: [
      { name: 'Load Board', price: '59.99', ctaLabel: 'Choose Plan', ctaHref: '/auth?mode=signup', trial: 'Modern load board only' },
      { name: 'Full Access', price: '99.99', badge: 'MOST POPULAR', badgeColor: 'bg-yellow-400 text-yellow-900', ctaLabel: 'Choose Plan', ctaHref: '/auth?mode=signup', trial: 'Full platform access' },
    ],
    rows: CARRIER_ROWS,
    footnote: 'All prices listed in USD. Prices and promotions valid for fleets of up to 10 trucks.',
  },
  broker: {
    key: 'broker',
    label: 'Broker',
    heading: 'Choose your plan and get started today',
    planLabel: 'Broker Plans',
    tiers: [
      { name: 'Standard', price: '49', ctaLabel: 'Choose Plan', ctaHref: '/auth?mode=signup', trial: 'Full platform access' },
      { name: 'Premium', price: '99.99', badge: 'MOST POPULAR', badgeColor: 'bg-yellow-400 text-yellow-900', ctaLabel: 'Choose Plan', ctaHref: '/auth?mode=signup', trial: 'Full platform access' },
      { name: 'Premium Plus', price: '149', badge: 'BEST VALUE', badgeColor: 'bg-emerald-500 text-white', ctaLabel: 'Choose Plan', ctaHref: '/auth?mode=signup', trial: 'Full platform access' },
    ],
    rows: BROKER_ROWS,
    footnote: 'All prices listed in USD. Prices and promotions valid for accounts with up to 10 seats.',
  },
  shipper: {
    key: 'shipper',
    label: 'Shipper',
    heading: 'Shipper Free Access',
    planLabel: 'Shipper Access',
    tiers: [
      {
        name: 'Free Access',
        price: '0',
        badge: 'FREE',
        badgeColor: 'bg-[#02857E]/10 text-[#02857E]',
        ctaLabel: 'Sign Up Free',
        ctaHref: '/auth?mode=signup&role=shipper',
        trial: 'Free during launch — post freight and manage your network',
      },
    ],
    rows: [
      { feature: 'Post unlimited loads to open marketplace', values: [true] },
      { feature: 'Post unlimited loads to LR Direct private network', values: [true] },
      { feature: 'Create private broker networks', values: [true] },
      { feature: 'Invite vetted brokers', values: [true] },
      { feature: 'Bid management tools', values: [true] },
      { feature: 'Direct messaging with carriers and brokers', values: [true] },
      { feature: 'Rate insights and lane benchmarks', values: [true] },
      { feature: 'Multi-stop loads (5 picks / 5 drops)', values: [true] },
      { feature: 'AI Voice Post & Bulk Upload', values: [true] },
      { feature: 'Document storage', values: [true] },
      { feature: 'Priority support', values: [true] },
      { feature: 'All ATLAS AI features', isAi: true, values: [true] },
    ],
    footnote: 'No credit card required. New shipper accounts require manual verification — our team reviews and approves within 24 hours.',
  },
};

type TabPlan = {
  key: RoleKey;
  label: string;
  name: string;
  price: string;
  priceSuffix: string;
  badge: string;
  badgeClass: string;
  ctaLabel: string;
  ctaHref: string;
  ctaDisabled?: boolean;
  rows: MatrixRow[];
  variant?: 'default' | 'premium';
  /** Value-first line rendered above the price (lead with value, not the $0). */
  tagline?: string;
  footnote: string;
  secondaryNote?: string;
};

const featuresOf = (rows: MatrixRow[]) =>
  rows.filter((r) => r.values[r.values.length - 1]);

const TAB_PLANS: Record<RoleKey, TabPlan> = {
  broker: {
    key: 'broker',
    label: 'Broker',
    name: 'Broker Access',
    price: '99.99',
    priceSuffix: '/month',
    badge: 'Premium',
    badgeClass: 'bg-[#02857E]/10 text-[#02857E] border border-[#02857E]/30',
    ctaLabel: 'Subscribe',
    ctaHref: '/auth?mode=signup',
    rows: featuresOf(BROKER_ROWS),
    footnote: 'All prices listed in USD. Billed monthly. Cancel anytime.',
  },
  carrier: {
    key: 'carrier',
    label: 'Carrier',
    name: 'Carrier Access',
    price: '99.99',
    priceSuffix: '/month',
    badge: 'Premium',
    badgeClass: 'bg-[#02857E]/10 text-[#02857E] border border-[#02857E]/30',
    ctaLabel: 'Subscribe',
    ctaHref: '/auth?mode=signup',
    rows: featuresOf(CARRIER_ROWS),
    footnote: 'All prices listed in USD. Billed monthly. Cancel anytime.',
  },
  shipper: {
    key: 'shipper',
    label: 'Shipper',
    name: 'Everything you need to move freight',
    tagline: 'Post loads, build your private broker network, and run awards end to end.',
    price: '0',
    priceSuffix: '/month · free during launch',
    badge: 'Full access',
    badgeClass: 'bg-[#02857E]/10 text-[#02857E] border border-[#02857E]/30',
    ctaLabel: 'Get started free',
    ctaHref: '/auth?mode=signup&role=shipper',
    rows: ROLE_MATRIX.shipper.rows,
    footnote: 'Every shipper feature is included — nothing is held back behind a paid tier. No credit card required. New shipper accounts require manual verification — our team reviews and approves within 24 hours.',
  },
};

const TAB_ORDER: RoleKey[] = ['broker', 'carrier', 'shipper'];

const PlanTabs = ({ active, onChange }: { active: RoleKey; onChange: (k: RoleKey) => void }) => (
  <div className="flex justify-center px-4">
    <div className="inline-flex flex-wrap justify-center gap-1.5 p-1.5 bg-white border border-gray-200 rounded-full shadow-sm max-w-full overflow-x-auto">
      {TAB_ORDER.map((key) => {
        const isActive = active === key;
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            className={cn(
              'px-5 sm:px-6 py-2.5 text-sm font-semibold rounded-full transition-all whitespace-nowrap',
              isActive
                ? 'bg-[#02857E] text-white shadow-sm'
                : 'text-gray-600 hover:text-[#02857E] hover:bg-[#02857E]/5'
            )}
          >
            {TAB_PLANS[key].label}s
          </button>
        );
      })}
    </div>
  </div>
);

const PlanCard = ({ plan }: { plan: TabPlan }) => {
  const isPremium = plan.variant === 'premium';
  return (
    <div
      className={cn(
        'rounded-2xl shadow-lg overflow-hidden border transition-all',
        isPremium
          ? 'bg-gradient-to-br from-slate-900 via-slate-900 to-purple-950 border-purple-500/30 text-white'
          : 'bg-white border-gray-200'
      )}
    >
      <div className={cn('px-6 sm:px-10 pt-8 pb-6', isPremium ? '' : 'border-b border-gray-100')}>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className={cn('inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase', plan.badgeClass)}>
            {plan.badge}
          </span>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <h3 className={cn('text-2xl sm:text-3xl font-bold mb-1', isPremium ? 'text-white' : 'text-gray-900')}>
              {plan.name}
            </h3>
            {plan.tagline && (
              <p className={cn('text-sm mb-2 max-w-md', isPremium ? 'text-slate-300' : 'text-gray-600')}>
                {plan.tagline}
              </p>
            )}
            <div className="flex items-baseline gap-1">
              <span className={cn('text-4xl sm:text-5xl font-extrabold', isPremium ? 'text-white' : 'text-[#02857E]')}>
                ${plan.price}
              </span>
              <span className={cn('text-sm', isPremium ? 'text-slate-300' : 'text-gray-500')}>
                {plan.priceSuffix}
              </span>
            </div>
          </div>
          <Link
            to={plan.ctaHref}
            aria-disabled={plan.ctaDisabled}
            onClick={(e) => { if (plan.ctaDisabled) e.preventDefault(); }}
            className={cn(
              'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all shadow-sm',
              plan.ctaDisabled && 'opacity-50 cursor-not-allowed',
              isPremium
                ? 'bg-purple-500 hover:bg-purple-400 text-white'
                : 'bg-[#02857E] hover:bg-[#026d67] text-white'
            )}
          >
            {plan.ctaLabel}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div className="px-6 sm:px-10 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          {plan.rows.map((row, i) => (
            <div key={i} className="flex items-start gap-3">
              <Check
                className={cn('w-5 h-5 mt-0.5 shrink-0', isPremium ? 'text-purple-300' : 'text-[#02857E]')}
                strokeWidth={3}
              />
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className={cn('text-sm font-semibold', isPremium ? 'text-white' : 'text-gray-900')}>
                    {row.feature}
                  </span>
                  {row.isNew && (
                    <span className="text-[10px] font-bold text-orange-500 uppercase tracking-wider">
                      New
                    </span>
                  )}
                  {row.isAi && (
                    <span className="inline-flex items-baseline gap-1 leading-none">
                      <span className={cn('text-[9px] uppercase tracking-wider', isPremium ? 'text-slate-400' : 'text-gray-400')}>
                        Powered by
                      </span>
                      <span
                        className="text-[11px] font-bold uppercase tracking-wider text-[#02857E] atlas-ai-glow"
                      >
                        ATLAS AI
                      </span>
                    </span>
                  )}
                </div>
                {row.description && (
                  <p className={cn('text-xs mt-0.5', isPremium ? 'text-slate-400' : 'text-gray-500')}>
                    {row.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className={cn('mt-8 pt-5 border-t text-xs', isPremium ? 'border-white/10 text-slate-400' : 'border-gray-100 text-gray-500')}>
          {plan.footnote}
          {plan.secondaryNote && (
            <div className="mt-2 text-gray-500">{plan.secondaryNote}</div>
          )}
        </div>
      </div>
    </div>
  );
};

type Tier = 'free' | 'premium';

const FREE_OVERRIDES: Partial<Record<RoleKey, Partial<TabPlan>>> = {
  broker: {
    name: 'Broker Free',
    price: '0',
    priceSuffix: '/forever',
    badge: 'Free — With Ads',
    badgeClass: 'bg-gray-100 text-gray-700 border border-gray-300',
    ctaLabel: 'Get Free Access',
    ctaHref: '/auth?mode=signup&role=broker&tier=free',
    secondaryNote: 'Free tier shows display ads · 0 boost credits/mo (purchase credits or upgrade for monthly grant).',
  },
};

const PREMIUM_CTA_HREFS: Partial<Record<RoleKey, string>> = {
  broker: '/auth?mode=signup&role=broker&tier=premium',
  carrier: '/auth?mode=signup&role=carrier&tier=premium',
};

const TierToggle = ({ tier, onChange }: { tier: Tier; onChange: (t: Tier) => void }) => (
  <div className="flex justify-center mb-6">
    <div className="inline-flex p-1 bg-gray-100 border border-gray-200 rounded-full">
      {(['free', 'premium'] as Tier[]).map((t) => {
        const active = tier === t;
        return (
          <button
            key={t}
            onClick={() => onChange(t)}
            className={cn(
              'px-5 py-2 text-sm font-semibold rounded-full transition-all capitalize',
              active ? 'bg-[#02857E] text-white shadow-sm' : 'text-gray-600 hover:text-[#02857E]'
            )}
          >
            {t}
          </button>
        );
      })}
    </div>
  </div>
);

// ============================================================
// Broker 2-card layout: Load Board ($59.99) / Full Access ($99.99)
// Monthly/Yearly toggle controls both prices.
// The $249.99 "LR Direct Full" tier is discontinued — its features are
// folded into Full Access.
// ============================================================
type BillingCycle = 'monthly' | 'yearly';

type BrokerPlanCol = {
  id: 'free' | 'board_only' | 'premium' | 'lr_direct_full';
  name: string;
  price: string;
  priceSuffix: string;
  subNote?: string;
  badge?: string;
  badgeClass?: string;
  /** Small secondary badge under the primary one (e.g. "Most Popular"). */
  secondaryBadge?: string;
  ctaLabel: string;
  ctaHref: string;
  ctaDisabled?: boolean;
  highlight?: boolean;
  /** 'lr' = LR Direct yellow brand accent (matches the header nav button). */
  accent?: 'teal' | 'lr';
  promoBadge?: string;
  promoBadgeClass?: string;
  features?: string[];
  featuresHeading?: string;
  exclusionNote?: string;
};

// Unified broker feature matrix — every card renders the SAME rows in the
// SAME order so feature lists align horizontally across columns.
// Access below mirrors the real gates in code:
//  - ATLAS / AI / board features have NO billing-mode gate (credit-metered only)
//    → included on every paid broker tier, board_only included.
//  - LR Direct bidding is the only tier-gated surface (BidActionGate,
//    DirectLoadDetailPanel, LRDirectGateContext).
//  - Monthly credits: grant-monthly-credits grants a flat 20/mo to every
//    non-free subscription; seats default to 1 on every tier (stripe-webhook).
type BrokerFeatureRow = {
  label: string;
  board: boolean | 'coming-soon';
  premium: boolean | 'coming-soon';
  isAi?: boolean;
  boardNote?: string;
  premiumNote?: string;
};

const BROKER_PLAN_FEATURES: BrokerFeatureRow[] = [
  { label: 'Unlimited Load Posting', board: true, premium: true },
  { label: 'Carrier & Truck Search', board: true, premium: true },
  { label: 'Mobile-Optimized Workspace', board: true, premium: true },
  { label: 'Truck Alerts', board: true, premium: true },
  { label: 'Saved Truck Searches', board: true, premium: true },
  { label: 'Document Storage', board: true, premium: true },
  { label: 'FMCSA Verification & Trust Scores', board: true, premium: true },
  { label: 'Drive-Time Calculations', board: true, premium: true },
  { label: 'Carrier Metrics', board: true, premium: true },
  { label: 'Multi-Stop Loads', board: true, premium: true, boardNote: '5 picks / 5 drops', premiumNote: '5 picks / 5 drops' },
  { label: 'Boosted Loads', board: true, premium: true },
  { label: 'Rate Check', board: true, premium: true, isAi: true },
  { label: 'Historical Rate Trends', board: true, premium: true, isAi: true },
  { label: 'ATLAS AI Rate Insights', board: true, premium: true, isAi: true },
  { label: 'Lane Predictor & Forecasting', board: true, premium: true, isAi: true },
  { label: 'Pattern Detector & Repricing', board: true, premium: true, isAi: true },
  { label: 'ATLAS Command Center', board: true, premium: true, isAi: true },
  { label: 'Market Pressure Index', board: true, premium: true, isAi: true },
  { label: 'AI Voice Post', board: true, premium: true, isAi: true },
  { label: 'AI Bulk Upload', board: true, premium: true, isAi: true },
  { label: 'Seat & Team Management', board: true, premium: true },
  { label: 'Credit Profile Auto-Fill', board: false, premium: true, isAi: true },
  { label: 'Monthly Boost Credits', board: true, premium: true, boardNote: '20/mo', premiumNote: '20/mo' },
  { label: 'LR Direct Private Network Bidding', board: false, premium: true },
  { label: 'LR Direct Bid Management', board: false, premium: true },
  { label: 'Direct Shipper Messaging on Awarded Loads', board: false, premium: true },
  { label: 'Live Load Room', board: false, premium: true },
  { label: 'Open Marketplace Bidding', board: false, premium: true },
  { label: 'Dedicated Account Manager', board: false, premium: true },
  { label: 'Priority Support', board: false, premium: true },
  { label: 'Early Access to New Features', board: false, premium: true },
];

const brokerPlansRaw = (cycle: BillingCycle, invited: boolean): BrokerPlanCol[] => [
  // 1. Load Board — $59.99/mo (board_only). No LR Direct bidding.
  cycle === 'yearly'
    ? {
        id: 'board_only',
        name: 'Load Board',
        price: '$575.90',
        priceSuffix: '/year',
        subNote: '$47.99/mo billed yearly · Save 20% · Load board only — no LR Direct bidding',
        badge: 'Load board only',
        badgeClass: 'bg-gray-200 text-gray-700',
        ctaLabel: 'Subscribe',
        ctaHref: '/auth?mode=signup&role=broker&tier=board_only_yearly',
        accent: 'teal',
        exclusionNote: 'Not included: LR Direct bidding — private shipper networks and the open LR Direct marketplace.',
      }
    : {
        id: 'board_only',
        name: 'Load Board',
        price: '$59.99',
        priceSuffix: '/month',
        subNote: 'Modern load board — search & post loads. Rate vs market on every load.',
        badge: 'Load board only',
        badgeClass: 'bg-gray-200 text-gray-700',
        ctaLabel: 'Subscribe',
        ctaHref: '/auth?mode=signup&role=broker&tier=board_only',
        accent: 'teal',
        exclusionNote: 'Not included: LR Direct bidding — private shipper networks and the open LR Direct marketplace.',
      },
  // 2. Full Access — $99.99/mo per user. Private networks + open marketplace
  //    + full load board. Internal billing IDs unchanged
  //    (tier=lr_direct_private monthly, tier=premium-yearly yearly).
  cycle === 'yearly'
    ? {
        id: 'premium',
        name: 'Full Access',
        price: '$959.90',
        priceSuffix: '/year',
        subNote: 'Private shipper networks, the open marketplace, and the full load board. $79.99/mo billed yearly, per user.',
        badge: 'Required for shipper invitations',
        badgeClass: 'bg-[#02857E] text-white',
        secondaryBadge: 'Invited Brokers',
        ctaLabel: invited ? 'Accept Invitation' : 'Activate Full Access',
        ctaHref: '/auth?mode=signup&role=broker&tier=premium-yearly',
        highlight: true,
        accent: 'lr',
      }
    : {
        id: 'premium',
        name: 'Full Access',
        price: '$99.99',
        priceSuffix: '/month',
        subNote: 'Private shipper networks, the open marketplace, and the full load board.',
        badge: 'Required for shipper invitations',
        badgeClass: 'bg-[#02857E] text-white',
        secondaryBadge: 'Invited Brokers',
        ctaLabel: invited ? 'Accept Invitation' : 'Activate Full Access',
        ctaHref: '/auth?mode=signup&role=broker&tier=lr_direct_private',
        highlight: true,
        accent: 'lr',
      },
];

/**
 * Broker plan columns. When an invite token is present every broker CTA keeps
 * `invite=<token>` so the network connection is never dropped, and the $99.99
 * card leads with the invitation badge.
 */
const getBrokerPlans = (
  cycle: BillingCycle,
  invited = false,
  inviteToken: string | null = null,
): BrokerPlanCol[] =>
  brokerPlansRaw(cycle, invited).map((p) =>
    inviteToken
      ? {
          ...p,
          ctaHref: `${p.ctaHref}${p.ctaHref.includes('?') ? '&' : '?'}invite=${encodeURIComponent(inviteToken)}`,
        }
      : p
  );



const BROKER_ID_TO_SELECTION: Partial<Record<BrokerPlanCol['id'], SelectionId>> = {
  free: 'broker_free', board_only: 'broker_board_only',
  premium: 'broker_premium', lr_direct_full: 'lr_direct_full',
};

const BillingCycleToggle = ({ cycle, onChange }: { cycle: BillingCycle; onChange: (c: BillingCycle) => void }) => (
  <div className="flex justify-center mb-6">
    <div className="inline-flex p-1 bg-gray-100 border border-gray-200 rounded-full">
      {(['monthly', 'yearly'] as BillingCycle[]).map((c) => {
        const active = cycle === c;
        return (
          <button
            key={c}
            onClick={() => onChange(c)}
            className={cn(
              'inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-full transition-all capitalize',
              active ? 'bg-[#02857E] text-white shadow-sm' : 'text-gray-600 hover:text-[#02857E]'
            )}
          >
            {c}
            {c === 'yearly' && (
              <span className={cn(
                'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide',
                active ? 'bg-emerald-400 text-emerald-900' : 'bg-emerald-500 text-white'
              )}>
                Save 20%
              </span>
            )}
          </button>
        );
      })}
    </div>
  </div>
);

const BrokerFeatureCell = ({
  status,
  label,
  note,
  isAi,
  accent = 'teal',
}: {
  status: boolean | 'coming-soon';
  label: string;
  note?: string;
  isAi?: boolean;
  accent?: 'teal' | 'lr';
}) => {
  const checkClass = accent === 'lr' ? 'text-lr-yellow' : 'text-[#02857E]';
  if (status === 'coming-soon') {
    return (
      <li className="flex items-start gap-2.5 py-2 border-b border-gray-100 last:border-b-0 min-h-[44px]">
        <Minus className="w-4 h-4 mt-0.5 text-gray-300 shrink-0" strokeWidth={3} />
        <span className="text-sm text-gray-500 italic">
          {label} <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-teal not-italic">Coming Soon</span>
        </span>
      </li>
    );
  }
  if (status === true) {
    return (
      <li className="flex items-start gap-2.5 py-2 border-b border-gray-100 last:border-b-0 min-h-[44px]">
        <Check className={cn('w-4 h-4 mt-0.5 shrink-0', checkClass)} strokeWidth={3} />
        <span className="text-sm text-gray-800">
          {label}
          {note && <span className="text-xs text-gray-500"> ({note})</span>}
          {isAi && (
            <span className="ml-1.5 inline-flex items-baseline gap-1 leading-none align-baseline">
              <span className="text-[8px] uppercase tracking-wider text-gray-400">Powered by</span>
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#02857E] atlas-ai-glow">ATLAS AI</span>
            </span>
          )}
        </span>
      </li>
    );
  }
  return (
    <li className="flex items-start gap-2.5 py-2 border-b border-gray-100 last:border-b-0 min-h-[44px]">
      <Minus className="w-4 h-4 mt-0.5 text-gray-300 shrink-0" strokeWidth={3} />
      <span className="text-sm text-gray-400">{label}</span>
    </li>
  );
};

const BrokerComparisonTable = ({
  selectedId,
  billingCycle,
  onCycleChange,
  inviteToken,
  inviteCompany,
}: {
  selectedId?: SelectionId | null;
  billingCycle: BillingCycle;
  onCycleChange: (c: BillingCycle) => void;
  inviteToken?: string | null;
  inviteCompany?: string | null;
}) => {
  const invited = !!inviteToken;
  const plans = getBrokerPlans(billingCycle, invited, inviteToken ?? null);
  const [waitlist, setWaitlist] = useState<WaitlistPlan | null>(null);
  const planKeys: Array<'board' | 'premium'> = ['board', 'premium'];

  return (
    <div className="mt-2">
      <BillingCycleToggle cycle={billingCycle} onChange={onCycleChange} />
      <FullAccessWaitlistModal plan={waitlist} onClose={() => setWaitlist(null)} />


      {/* "Which plan do I need?" — answered before the cards. */}

      <BrokerPlanGuidanceBanner
        invited={invited}
        shipperName={inviteCompany}
      />

      {/* 2-card grid — Load Board ($59.99), Full Access ($99.99) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch max-w-4xl mx-auto">
        {plans.map((plan, idx) => {
          const sid = BROKER_ID_TO_SELECTION[plan.id]!;
          const isSel = selectedId === sid;
          const colKey = planKeys[idx];
          const isLr = plan.accent === 'lr';
          const isPrivate = plan.id === 'premium';
          // Same rows everywhere, but included rows render first so the card
          // leads with what you get and ends with what you don't.
          const noteFor = (row: BrokerFeatureRow) =>
            colKey === 'board' ? row.boardNote : row.premiumNote;
          const included = BROKER_PLAN_FEATURES.filter((r) => r[colKey] !== false);
          const excluded = BROKER_PLAN_FEATURES.filter((r) => r[colKey] === false);
          return (
            <div
              key={plan.id}
              data-selection-card={sid}
              className={cn(
                'relative rounded-2xl border bg-white shadow-sm p-6 flex flex-col transition-all',
                isLr
                  ? 'border-lr-yellow/60 bg-lr-yellow/10'
                  : plan.highlight ? 'border-[#02857E]/30 bg-[#02857E]/5' : 'border-gray-200',
                isPrivate && invited &&
                  'border-[#02857E] bg-[#f1faf9] ring-4 ring-[#02857E]/35 ring-offset-2 z-10',
                isSel && 'ring-2 ring-[#02857E] ring-offset-2 z-10'
              )}
            >
              {isSel && plan.id !== 'free' && <SelectedBadge className="absolute -top-2.5 left-4" />}
              {plan.promoBadge && (
                <span
                  className={cn(
                    'inline-flex items-center self-start px-3 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase whitespace-nowrap shadow-sm mb-3',
                    plan.promoBadgeClass
                  )}
                >
                  {plan.promoBadge}
                </span>
              )}
              {plan.badge && plan.id === 'premium' ? (
                /* Invited brokers need this signal to read first, so the
                   invitation badge sits inline above the plan name. */
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span
                    className={cn(
                      'inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase shadow-sm',
                      plan.badgeClass
                    )}
                  >
                    {plan.badge}
                  </span>
                  {plan.secondaryBadge && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase bg-yellow-400 text-yellow-900">
                      {plan.secondaryBadge}
                    </span>
                  )}
                </div>
              ) : plan.badge ? (
                <span
                  className={cn(
                    'absolute top-3 right-3 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase whitespace-nowrap shadow-sm',
                    plan.badgeClass
                  )}
                >
                  {plan.badge}
                </span>
              ) : null}
              <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>

              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-[#02857E]">{plan.price}</span>
                <span className="text-sm text-gray-500">{plan.priceSuffix}</span>
              </div>
              {plan.id === 'premium' && (
                <p className="mt-1 text-xs font-semibold text-[#02857E]">
                  Per user. Required for shipper invitations.
                </p>
              )}
              {plan.subNote && (
                <p className="mt-2 text-xs text-gray-500 min-h-[2.25rem]">{plan.subNote}</p>
              )}

              {/* CTA up top so all CTAs align across columns */}
              <div className="mt-4">
                <Link
                  to={plan.ctaHref}
                  onClick={(e) => {
                    if (plan.id === 'premium') { e.preventDefault(); setWaitlist({ role: 'broker', cycle: billingCycle }); return; }
                    writeSelection(sid);
                  }}
                  className={cn(
                    'w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition-all shadow-sm',
                    isLr
                      ? 'bg-lr-yellow hover:bg-lr-yellow-hover text-gray-900'
                      : plan.highlight
                        ? 'bg-[#02857E] hover:bg-[#026d67] text-white'
                        : 'bg-white border border-[#02857E] text-[#02857E] hover:bg-[#02857E]/5'
                  )}
                >
                  {plan.ctaLabel}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Divider */}
              <div className="mt-6 mb-2 border-t border-gray-200/80" />

              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                What's included
              </p>

              <ul className="mt-1">
                {included.map((row, i) => (
                  <BrokerFeatureCell
                    key={`inc-${i}`}
                    status={row[colKey]}
                    label={row.label}
                    note={noteFor(row)}
                    isAi={row.isAi}
                    accent={plan.accent}
                  />
                ))}
              </ul>

              {excluded.length > 0 && (
                <>
                  <p className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
                    Not included
                  </p>
                  <ul>
                    {excluded.map((row, i) => (
                      <BrokerFeatureCell
                        key={`exc-${i}`}
                        status={false}
                        label={row.label}
                        accent={plan.accent}
                      />
                    ))}
                  </ul>
                </>
              )}

              {plan.exclusionNote && (
                <p className="mt-4 text-[11px] leading-relaxed text-gray-500 border-t border-gray-200/80 pt-3">
                  {plan.exclusionNote}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-center text-xs text-gray-500 max-w-2xl mx-auto">
        Cancel anytime. A credit card is required at signup to help prevent fraud and keep the LaneRunner network trusted. All prices listed in USD.
      </p>
    </div>
  );
};




// ============================================================
// Carrier tab — mirrors the broker tab exactly: monthly/yearly
// toggle + two cards (Load Board $59.99 / Full Access $99.99).
// Stale $39.99 / $119 / $149.99-combo blocks retired.
// CTAs are intentionally non-functional placeholders (Stripe
// wiring lands in a separate step).
// ============================================================
type CarrierFeatureRow = {
  label: string;
  board: boolean;
  full: boolean;
  isAi?: boolean;
  boardNote?: string;
  fullNote?: string;
};

const CARRIER_PLAN_FEATURES: CarrierFeatureRow[] = [
  { label: 'Full modern load board', board: true, full: true },
  { label: 'Search loads, unlimited', board: true, full: true },
  { label: 'Post your truck', board: true, full: true },
  { label: 'Verified broker information and FMCSA records', board: true, full: true },
  { label: 'Route maps, mileage, and drive time', board: true, full: true },
  { label: 'Saved searches and live load alerts', board: true, full: true },
  { label: 'Multi-stop loads', board: true, full: true, boardNote: '5 picks / 5 drops', fullNote: '5 picks / 5 drops' },
  { label: 'Post your truck and get auto-matched', board: false, full: true },
  { label: 'ATLAS AI rate insights and lane intelligence', board: false, full: true, isAi: true },
  { label: 'Voice posting', board: false, full: true, isAi: true },
  { label: 'Dispatch, tracking, and docs on every load', board: false, full: true },
  { label: 'Direct messaging', board: false, full: true },
  { label: 'Ad-free workspace', board: false, full: true },
  { label: 'Monthly boost credits', board: false, full: true, fullNote: '20/mo' },
  { label: 'Direct shipper freight', board: false, full: true },
  { label: 'Open marketplace bidding', board: false, full: true },
];

type CarrierPlanCard = {
  id: 'board_only' | 'full';
  name: string;
  price: string;
  priceSuffix: string;
  subNote?: string;
  badge?: string;
  badgeClass?: string;
  ctaLabel: string;
  ctaHref: string;
  highlight?: boolean;
  accent?: 'teal' | 'lr';
  exclusionNote?: string;
};

const carrierPlans = (cycle: BillingCycle): CarrierPlanCard[] => [
  cycle === 'yearly'
    ? {
        id: 'board_only',
        name: 'Load Board',
        price: '$575.90',
        priceSuffix: '/year',
        subNote: '$47.99/mo billed yearly · Save 20% · The modern board on its own. No direct shipper bidding.',
        badge: 'Load board only',
        badgeClass: 'bg-gray-200 text-gray-700',
        ctaLabel: 'Subscribe',
        ctaHref: '/auth?mode=signup&role=carrier&tier=carrier_board_yearly',
        accent: 'teal',
        exclusionNote: 'Not included: direct shipper freight and open marketplace bidding.',
      }
    : {
        id: 'board_only',
        name: 'Load Board',
        price: '$59.99',
        priceSuffix: '/month',
        subNote: 'The modern board on its own. No direct shipper bidding.',
        badge: 'Load board only',
        badgeClass: 'bg-gray-200 text-gray-700',
        ctaLabel: 'Subscribe',
        ctaHref: '/auth?mode=signup&role=carrier&tier=carrier_board',
        accent: 'teal',
        exclusionNote: 'Not included: direct shipper freight and open marketplace bidding.',
      },
  cycle === 'yearly'
    ? {
        id: 'full',
        name: 'Full Access',
        price: '$959.90',
        priceSuffix: '/year',
        subNote: 'Direct shipper freight, the open marketplace, and the full load board. $79.99/mo billed yearly, per user.',
        badge: 'Full platform access',
        badgeClass: 'bg-[#02857E] text-white',
        ctaLabel: 'Activate Full Access',
        ctaHref: '/auth?mode=signup&role=carrier&tier=carrier_full_yearly',
        highlight: true,
        accent: 'lr',
      }
    : {
        id: 'full',
        name: 'Full Access',
        price: '$99.99',
        priceSuffix: '/month',
        subNote: 'Direct shipper freight, the open marketplace, and the full load board.',
        badge: 'Full platform access',
        badgeClass: 'bg-[#02857E] text-white',
        ctaLabel: 'Activate Full Access',
        ctaHref: '/auth?mode=signup&role=carrier&tier=carrier_full',
        highlight: true,
        accent: 'lr',
      },
];

const CarrierComparisonTable = ({
  billingCycle,
  onCycleChange,
}: {
  billingCycle: BillingCycle;
  onCycleChange: (c: BillingCycle) => void;
}) => {
  const plans = carrierPlans(billingCycle);
  const [waitlist, setWaitlist] = useState<WaitlistPlan | null>(null);
  const planKeys: Array<'board' | 'full'> = ['board', 'full'];

  return (
    <div className="mt-2">
      <BillingCycleToggle cycle={billingCycle} onChange={onCycleChange} />
      <FullAccessWaitlistModal plan={waitlist} onClose={() => setWaitlist(null)} />


      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch max-w-4xl mx-auto">
        {plans.map((plan, idx) => {
          const colKey = planKeys[idx];
          const isLr = plan.accent === 'lr';
          const noteFor = (row: CarrierFeatureRow) =>
            colKey === 'board' ? row.boardNote : row.fullNote;
          const included = CARRIER_PLAN_FEATURES.filter((r) => r[colKey] !== false);
          const excluded = CARRIER_PLAN_FEATURES.filter((r) => r[colKey] === false);
          return (
            <div
              key={plan.id}
              className={cn(
                'relative rounded-2xl border bg-white shadow-sm p-6 flex flex-col transition-all',
                isLr
                  ? 'border-lr-yellow/60 bg-lr-yellow/10'
                  : plan.highlight ? 'border-[#02857E]/30 bg-[#02857E]/5' : 'border-gray-200'
              )}
            >
              {plan.badge && plan.id === 'full' ? (
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span
                    className={cn(
                      'inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase shadow-sm',
                      plan.badgeClass
                    )}
                  >
                    {plan.badge}
                  </span>
                </div>
              ) : plan.badge ? (
                <span
                  className={cn(
                    'absolute top-3 right-3 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase whitespace-nowrap shadow-sm',
                    plan.badgeClass
                  )}
                >
                  {plan.badge}
                </span>
              ) : null}

              <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>

              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-[#02857E]">{plan.price}</span>
                <span className="text-sm text-gray-500">{plan.priceSuffix}</span>
              </div>
              {plan.id === 'full' && (
                <p className="mt-1 text-xs font-semibold text-[#02857E]">Per user.</p>
              )}
              {plan.subNote && (
                <p className="mt-2 text-xs text-gray-500 min-h-[2.25rem]">{plan.subNote}</p>
              )}

              {/* Live CTA — routes to signup with the carrier tier preselected. */}
              <div className="mt-4">
                <Link
                  to={plan.ctaHref}
                  onClick={(e) => {
                    if (plan.id === 'full') { e.preventDefault(); setWaitlist({ role: 'carrier', cycle: billingCycle }); }
                  }}
                  className={cn(
                    'w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition-all shadow-sm',
                    isLr
                      ? 'bg-lr-yellow hover:bg-lr-yellow-hover text-gray-900'
                      : plan.highlight
                        ? 'bg-[#02857E] hover:bg-[#026d67] text-white'
                        : 'bg-white border border-[#02857E] text-[#02857E] hover:bg-[#02857E]/5'
                  )}
                >
                  {plan.ctaLabel}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="mt-6 mb-2 border-t border-gray-200/80" />

              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                What's included
              </p>

              <ul className="mt-1">
                {included.map((row, i) => (
                  <BrokerFeatureCell
                    key={`inc-${i}`}
                    status={row[colKey]}
                    label={row.label}
                    note={noteFor(row)}
                    isAi={row.isAi}
                    accent={plan.accent}
                  />
                ))}
              </ul>

              {excluded.length > 0 && (
                <>
                  <p className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
                    Not included
                  </p>
                  <ul>
                    {excluded.map((row, i) => (
                      <BrokerFeatureCell
                        key={`exc-${i}`}
                        status={false}
                        label={row.label}
                        accent={plan.accent}
                      />
                    ))}
                  </ul>
                </>
              )}

              {plan.exclusionNote && (
                <p className="mt-4 text-[11px] leading-relaxed text-gray-500 border-t border-gray-200/80 pt-3">
                  {plan.exclusionNote}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-center text-xs text-gray-500 max-w-2xl mx-auto">
        Cancel anytime. A credit card is required at signup to help prevent fraud and keep the LaneRunner network trusted. All prices listed in USD.
      </p>
    </div>
  );
};





const PricingPage = () => {
  const [searchParams] = useSearchParams();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeTab, setActiveTab] = useState<RoleKey>('broker');
  const [tier, setTier] = useState<Tier>('premium');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [selectedId, setSelectedId] = useState<SelectionId | null>(null);
  // Invited brokers: token from the URL, falling back to the stash written by
  // /brokers or an earlier pricing visit. Drives the guidance banner + $99.99
  // card treatment, and is appended to every broker CTA.
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteCompany, setInviteCompany] = useState<string | null>(null);

  useEffect(() => {
    const fromUrl = searchParams.get('invite');
    if (fromUrl) { setInviteToken(fromUrl); return; }
    try {
      const stashed = sessionStorage.getItem('lr_pending_network_invite_token');
      if (stashed) setInviteToken(stashed);
    } catch { /* ignore */ }
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    if (!inviteToken) return;
    (async () => {
      try {
        const { data } = await (supabase as any).rpc('preview_network_invite', { _token: inviteToken });
        const row = Array.isArray(data) ? data[0] : data;
        if (!cancelled && row?.shipper_company_name) {
          setInviteCompany(displayCompanyName(row.shipper_company_name));
        }
      } catch { /* banner falls back to generic copy */ }
    })();
    return () => { cancelled = true; };
  }, [inviteToken]);

  // Apply ?tier=, ?selected=, or sessionStorage selection: switch tab + persist + highlight.
  useEffect(() => {
    const tierParam = searchParams.get('tier');
    const inviteParam = searchParams.get('invite');
    const roleParam = (searchParams.get('role') || '').toLowerCase();
    // Keep the network-invite token alive across pricing → signup → Stripe.
    if (inviteParam) {
      try { sessionStorage.setItem('lr_pending_network_invite_token', inviteParam); } catch {}
    }

    // Broker-tab tier shortcuts (LR Direct merged into Broker tab).
    const tierShortcut: Record<string, { sel: SelectionId; cycle: BillingCycle }> = {
      // Discontinued $249.99 tier — legacy links land on Full Access ($99.99).
      'lr_direct':         { sel: 'broker_premium', cycle: 'monthly' },
      'lr_direct_yearly':  { sel: 'broker_premium', cycle: 'yearly'  },
      'lr_direct_private': { sel: 'broker_premium', cycle: 'monthly' },
      'premium-monthly':   { sel: 'broker_premium', cycle: 'monthly' },
      'premium-yearly':    { sel: 'broker_premium', cycle: 'yearly'  },
    };
    // Invited brokers land here from the shipper email — preselect the
    // $99.99 LR Direct + Load Board card (the tier that unlocks their
    // shipper's private network).
    const effectiveTierParam =
      tierParam || (inviteParam && (roleParam === 'broker' || !roleParam) ? 'premium-monthly' : null);
    if (effectiveTierParam && tierShortcut[effectiveTierParam]) {
      const { sel, cycle } = tierShortcut[effectiveTierParam];
      setSelectedId(sel);
      setActiveTab('broker');
      setBillingCycle(cycle);
      try { sessionStorage.setItem(SELECTION_KEY, sel); } catch {}
      if (typeof window !== 'undefined' && window.innerWidth < 768) {
        setTimeout(() => {
          const el = document.querySelector(`[data-selection-card="${sel}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 250);
      }
      return;
    }

    let raw: string | null = searchParams.get('selected');
    if (!raw) {
      try { raw = sessionStorage.getItem(SELECTION_KEY); } catch { raw = null; }
    }
    if (!isSelectionId(raw)) return;
    setSelectedId(raw);
    setActiveTab(SELECTION_TO_TAB[raw]);
    if (raw === 'broker_yearly') setBillingCycle('yearly');
    try { sessionStorage.setItem(SELECTION_KEY, raw); } catch {}
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setTimeout(() => {
        const el = document.querySelector(`[data-selection-card="${raw}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const supportsTier = activeTab === 'broker' || activeTab === 'carrier';
  const basePlan = TAB_PLANS[activeTab];
  const premiumPlan: TabPlan = supportsTier && PREMIUM_CTA_HREFS[activeTab]
    ? { ...basePlan, ctaHref: PREMIUM_CTA_HREFS[activeTab]! }
    : basePlan;
  const activePlan: TabPlan = supportsTier && tier === 'free' && FREE_OVERRIDES[activeTab]
    ? { ...basePlan, ...FREE_OVERRIDES[activeTab]! }
    : premiumPlan;

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  usePageMeta({
    fullTitle: true,
    title: 'Pricing | LaneRunner',
    description: 'Transparent pricing for every role in freight. Plans for brokers, carriers, and shippers.'
  });

  const faqItems = [
    {
      question: "What is LaneRunner?",
      answer: "A modern freight platform with two sides: a full load board for searching and posting freight, and LR Direct, where shippers post loads straight to the brokers they trust. Shippers, brokers, and carriers all work in one place."
    },
    {
      question: "What is LR Direct?",
      answer: "LR Direct lets shippers post freight directly to their private network of vetted brokers, or to the open marketplace. Brokers bid, win the load, and keep the direct shipper relationship. LaneRunner takes no margin on the freight."
    },
    {
      question: "Does LaneRunner take a cut of the freight?",
      answer: "No. Zero margin on any load. Everyone pays a flat monthly subscription (shippers free) and keeps 100% of their freight."
    },
    {
      question: "I'm a shipper: is it really free?",
      answer: "Yes. Post your freight, build your private broker network, and manage loads at no cost. New shipper accounts are reviewed within 24 hours to keep the network clean."
    },
    {
      question: "I'm a broker or carrier: which plan do I need?",
      answer: "Two plans. Full Access ($99.99/mo per user) unlocks the full platform including private network bidding, open marketplace bidding, and the load board. If you only want the load board, choose Load Board ($59.99/mo). Cancel anytime."
    },
    {
      question: "How are companies verified?",
      answer: "Carriers and brokers are verified through FMCSA integration, including safety ratings, insurance status, and operating authority. Shipper accounts are manually reviewed before approval."
    },
    {
      question: "Is there a mobile app?",
      answer: "Yes. The full platform is in the LaneRunner app, available on Google Play now, with the App Store version coming soon."
    },
    {
      question: "Can I switch plans or cancel anytime?",
      answer: "Yes. Move between plans whenever you want, and cancel anytime with no long-term contract."
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <LandingHeader activePage="pricing" />

      {/* SECTION 1: Hero intro */}
      <section className="relative pt-32 pb-10 overflow-hidden bg-gradient-to-br from-[#f0f7f6] via-[#f5f7fa] to-[#f8faf5]">
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: `linear-gradient(#1ab7aa 1px, transparent 1px), linear-gradient(90deg, #1ab7aa 1px, transparent 1px)`,
          backgroundSize: '48px 48px'
        }} />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl mb-4 leading-tight">
            <span className="text-gray-900">Pricing built for every </span>
            <span className="text-[#1ab7aa]">role in freight</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Transparent access for brokers, carriers, and shippers.
          </p>
        </div>
      </section>

      {/* SECTION 2: Tabbed single-plan layout */}
      <section className="bg-gradient-to-b from-[#f5f9f8] to-white pt-10 pb-20">
        <PlanTabs active={activeTab} onChange={(k) => { setActiveTab(k); setTier('premium'); }} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-10">
          {activeTab !== 'broker' && activeTab !== 'carrier' && activeTab !== 'shipper' && (
            <div className="text-center mb-6">
              <p className="text-gray-500 text-sm">
                Cancel anytime. A credit card is required at signup to help prevent fraud and keep the LaneRunner network trusted.
              </p>
            </div>
          )}
          {activeTab === 'broker' ? (
            <BrokerComparisonTable selectedId={selectedId} billingCycle={billingCycle} onCycleChange={setBillingCycle} inviteToken={inviteToken} inviteCompany={inviteCompany} />
          ) : activeTab === 'carrier' ? (
            <CarrierComparisonTable billingCycle={billingCycle} onCycleChange={setBillingCycle} />
          ) : (
            <>
              {supportsTier && <TierToggle tier={tier} onChange={setTier} />}
              <div
                key={`${activeTab}-${supportsTier ? tier : 'x'}`}
                data-selection-card={activeTab === 'shipper' ? 'shipper' : undefined}
                className={cn(
                  'animate-in fade-in duration-200 rounded-2xl transition-all',
                  activeTab === 'shipper' && selectedId === 'shipper' && 'ring-2 ring-[#02857E] ring-offset-2'
                )}
              >
                {activeTab === 'shipper' && selectedId === 'shipper' && (
                  <div className="mb-3 flex justify-center"><SelectedBadge /></div>
                )}
                <PlanCard plan={activePlan} />
              </div>
            </>
          )}
        </div>
      </section>




      {/* SECTION 3: Trust + Conversion */}
      <section className="py-12 px-6 bg-gradient-to-br from-[#f0f7f6] via-[#f5f7fa] to-[#f8faf5]">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-8 text-gray-600">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-[#1ab7aa]" />
              <span>No contracts. Cancel anytime.</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-[#1ab7aa]" />
              <span>Built for carriers, brokers, and shippers.</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-[#1ab7aa]" />
              <span>Real loads. Real marketplace.</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: FAQ */}
      <section className="relative py-24 px-6 bg-white overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#1ab7aa]/10 rounded-full blur-3xl" />

        <div className="relative max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl text-center mb-12">
            <span className="text-gray-900">Common </span>
            <span className="text-[#1ab7aa]">Questions</span>
          </h2>

          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white shadow-sm border border-gray-200 rounded-xl px-6 hover:shadow-lg hover:border-[#1ab7aa]/40 transition-all duration-300"
              >
                <AccordionTrigger className="text-gray-900 hover:text-[#1ab7aa] text-left py-6">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-500 pb-6">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* SECTION 4b: Always Cheaper. Always Fair. */}
      <section className="relative py-16 sm:py-20 lg:py-28 overflow-hidden bg-white">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-16 sm:w-20 h-16 sm:h-20 bg-[#1ab7aa]/10 rounded-full mx-auto flex items-center justify-center mb-6 sm:mb-8">
            <DollarSign className="w-8 sm:w-10 h-8 sm:h-10 text-[#1ab7aa]" />
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-5xl text-gray-900 mb-6 sm:mb-8 px-2">
            <span className="text-[#1ab7aa]">Always Cheaper</span>. Always Fair.
          </h2>

          <div className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed px-4 space-y-4 sm:space-y-6">
            <p>You shouldn't be paying hundreds a month for a basic load board.</p>
            <p>LaneRunner gives you more — for less.</p>
            <p className="text-lg sm:text-xl font-bold text-[#1ab7aa]">
              No contracts. No hidden fees. No surprises.
            </p>
          </div>
        </div>
      </section>


      {/* Footer */}
      <footer className="relative py-16 px-6 border-t border-white/10" style={{ backgroundColor: '#0A0E15' }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <img src={lanerunnerLogoWhite} alt="LaneRunner Logo" className="h-14 sm:h-16 mb-4" />
              <p className="text-gray-400 text-sm">The modern freight marketplace.</p>
            </div>
            <div>
              <h4 className="text-white mb-4">Platform</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/shippers" className="hover:text-[#1ab7aa] transition-colors">For Shippers</Link></li>
                <li><Link to="/brokers" className="hover:text-[#1ab7aa] transition-colors">For Brokers</Link></li>
                <li><Link to="/carriers" className="hover:text-[#1ab7aa] transition-colors">For Carriers</Link></li>
                <li><Link to="/pricing" className="hover:text-[#1ab7aa] transition-colors">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/about" className="hover:text-[#1ab7aa] transition-colors">About</Link></li>
                <li><Link to="/investors" className="hover:text-[#1ab7aa] transition-colors">Investors</Link></li>
                <li><Link to="/affiliates" className="hover:text-[#1ab7aa] transition-colors">Affiliate Program</Link></li>
                <li><Link to="/privacy" className="hover:text-[#1ab7aa] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-[#1ab7aa] transition-colors">Terms of Service</Link></li>
                <li><Link to="/cancellation-policy" className="hover:text-[#1ab7aa] transition-colors">Cancellation Policy</Link></li>
                <li><Link to="/cookies" className="hover:text-[#1ab7aa] transition-colors">Cookie Policy</Link></li>
                <li><button type="button" onClick={() => window.dispatchEvent(new Event('lr-open-cookie-settings'))} className="hover:text-[#1ab7aa] transition-colors text-left">Cookie Preferences</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white mb-4">Connect</h4>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-[#1ab7aa] transition-colors"><Facebook className="w-5 h-5" /></a>
                <a href="#" className="text-gray-400 hover:text-[#1ab7aa] transition-colors"><Instagram className="w-5 h-5" /></a>
                <a href="#" className="text-gray-400 hover:text-[#1ab7aa] transition-colors"><Linkedin className="w-5 h-5" /></a>
                <a href="#" className="text-gray-400 hover:text-[#1ab7aa] transition-colors"><Twitter className="w-5 h-5" /></a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-gray-500 text-sm">
            © 2026 LaneRunner. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Scroll to Top */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-50 p-3 bg-[#1ab7aa] text-black rounded-full shadow-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(26,183,170,0.4)] hover:scale-110 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <ChevronUp className="w-6 h-6" />
      </button>
    </div>
  );
};

export default PricingPage;
